/**
 * 65.49 step 1: the homing core — game homing, not a simulation (the owner: "we just want to mock it out and make it feel fun
 * in the game system, such that it's not a guaranteed hit, but there's some coherence to what's a likely hit and what's an
 * unlikely hit"). A body out of its tube coasts, then boosts to its top speed and turns its heading toward its target by at
 * most its turn rate a second; it hits when it passes within its fuse of the target's body. Whether it hits follows from what
 * the shooter can read: a ship flying straight is caught, one crossing close or breaking hard late can outturn it. No dice.
 *
 * Pure: the world's queries come in (`HomingWorld`: where a target is, the sight test, how near a stretch of flight passes the
 * target's body, and the decoy hook), so the anti-dropship missile (step 2), interceptors and anything else that homes reuse
 * it. Deterministic in dmath; a few live at once, so plain state. The lock (`stepLock`) is the seeker's rule before the shot;
 * `lockView` is what the HUD and the tone read of it (ui/antiDropshipMissileHud.ts draws it; nothing here draws).
 */
import type { HomingBody, MissileLock, Vec3 } from '../types';
import { vecAdd, vecSub, vecScale, vecDot, vecLength, vecNormalize, vecCross, vec3 } from '../math';
import { dacos, dcos, dsin } from './dmath';

/** A homing body's dials (people, seconds, radians): constants/weapons.ts ANTI_DROPSHIP_MISSILE_DIALS.homing's shape. */
export interface HomingDials { launchSpeed: number; coast: number; boost: number; topSpeed: number; turn: number; fuse: number; life: number; leadGain: number; lostAfter: number }
/** The seeker's dials (radians, seconds, people): ANTI_DROPSHIP_MISSILE_DIALS.lock's shape. */
export interface LockDials { cone: number; hold: number; reach: number }

/** Where a target is and how it moves (world units, a second). */
export interface HomingTarget { pos: Vec3; vel: Vec3 }
/** The world as a homing body asks it. */
export interface HomingWorld {
  /** the target with this id, or null when it is gone (destroyed, landed out of play) */
  target(id: number): HomingTarget | null;
  /** whether `to` is in sight from `from` (the game's sight test: terrain, the horizon) */
  sees(from: Vec3, to: Vec3): boolean;
  /** how near the stretch of flight `a` → `b` passes the target's body (its hit parts; world units), Infinity when gone */
  pass(a: Vec3, b: Vec3, id: number): number;
  /** decoys (flares, later): the target the body chases from now on; absent keeps it */
  retarget?(body: HomingBody): number | null;
}

/** A body launched from `pos` along `dir` (unit) at its launch speed, chasing `target` (null: fired without a lock, it flies straight). */
export function launchHoming(pos: Vec3, dir: Vec3, target: number | null, d: HomingDials, person: number): HomingBody {
  return { pos, vel: vecScale(vecNormalize(dir), d.launchSpeed * person), age: 0, target, unseen: 0 };
}

/** `from` (unit) turned toward `to` (unit) by at most `max` radians, on the great circle between them. */
export function turnToward(from: Vec3, to: Vec3, max: number): Vec3 {
  const c = Math.max(-1, Math.min(1, vecDot(from, to)));
  const ang = dacos(c);
  if (ang <= max) return to;
  let perp = vecSub(to, vecScale(from, c));
  if (vecLength(perp) < 1e-12) {
    // straight behind: any way round (the one nearest +y, else +x — fixed, so the same each run)
    perp = vecCross(vecCross(from, vec3(0, 1, 0)), from);
    if (vecLength(perp) < 1e-12) perp = vecCross(vecCross(from, vec3(1, 0, 0)), from);
  }
  perp = vecNormalize(perp);
  return vecNormalize(vecAdd(vecScale(from, dcos(max)), vecScale(perp, dsin(max))));
}

/**
 * One step of `dt` seconds: the body ages, keeps or loses its target (gone, decoyed, or out of sight past `lostAfter`),
 * speeds up after its coast and steers toward where the target will be (`leadGain` of the way along its velocity over the time
 * to go at the body's speed), then moves; `hit` the target's id when the step's stretch passes within the fuse of its body
 * (armed after the coast), `spent` when its life is over (the host removes it; it falls dead).
 */
export function stepHoming(b: HomingBody, d: HomingDials, world: HomingWorld, dt: number, person: number): { body: HomingBody; hit?: number; spent?: true } {
  const age = b.age + dt;
  if (age > d.life) return { body: { ...b, age }, spent: true };
  let target = world.retarget ? world.retarget(b) : b.target, unseen = b.unseen;
  let t: HomingTarget | null = target === null ? null : world.target(target);
  if (target !== null && !t) target = null;
  if (t) {
    unseen = world.sees(b.pos, t.pos) ? 0 : unseen + dt;
    if (unseen > d.lostAfter) { target = null; t = null; }
  }
  let speed = vecLength(b.vel), dir = speed > 0 ? vecScale(b.vel, 1 / speed) : vec3(0, 1, 0);
  const lit = age >= d.coast;
  if (lit) {
    speed = Math.min(d.topSpeed * person, speed + d.boost * person * dt);
    if (t && unseen === 0) {
      const togo = vecLength(vecSub(t.pos, b.pos)) / speed;
      const aim = vecAdd(t.pos, vecScale(t.vel, d.leadGain * togo));
      const want = vecSub(aim, b.pos);
      if (vecLength(want) > 0) dir = turnToward(dir, vecNormalize(want), d.turn * dt);
    }
  }
  const pos = vecAdd(b.pos, vecScale(dir, speed * dt));
  const body: HomingBody = { pos, vel: vecScale(dir, speed), age, target, unseen };
  if (lit && target !== null && world.pass(b.pos, pos, target) <= d.fuse * person) return { body, hit: target };
  return { body };
}

/** A ship the seeker may hold: its id and where it is (world units). */
export interface LockCandidate { id: number; pos: Vec3; /** 69.59 step 3 (DECISIONS 286 (4)): the share of the seeker's reach it is locked within (a helicopter's IR suppressors: HELI.irSuppressor); absent: 1 */ reach?: number }

/** The seeker off. */
export const LOCK_OFF: MissileLock = { state: 'off', target: null, held: 0 };

/**
 * One step of the seeker: off unless `on` (the weapon up); else the ship in sight nearest the aim inside the cone and within
 * reach is held (a new one starts its hold again; the one held keeps counting), locked once held `hold` seconds; none held,
 * it searches.
 */
export function stepLock(prev: MissileLock, on: boolean, eye: Vec3, aim: Vec3, ships: readonly LockCandidate[], sees: (from: Vec3, to: Vec3) => boolean, d: LockDials, dt: number, person: number): MissileLock {
  if (!on) return LOCK_OFF;
  const a = vecNormalize(aim), cosCone = dcos(d.cone), reach = d.reach * person;
  let best: LockCandidate | null = null, bestCos = -2;
  for (const s of ships) {
    const to = vecSub(s.pos, eye), dist = vecLength(to);
    if (!(dist > 0) || dist > reach * (s.reach ?? 1)) continue;
    const c = vecDot(to, a) / dist;
    // the nearest the aim wins; a tie keeps the one held, else the lower id (the same each run)
    const better = c > bestCos || (c === bestCos && best !== null && (s.id === prev.target || (best.id !== prev.target && s.id < best.id)));
    if (c < cosCone || !better || !sees(eye, s.pos)) continue;
    best = s; bestCos = c;
  }
  if (!best) return { state: 'search', target: null, held: 0 };
  const held = best.id === prev.target && prev.state !== 'off' && prev.state !== 'search' ? prev.held + dt : dt;
  return { state: held >= d.hold ? 'lock' : 'track', target: best.id, held };
}

/** What the HUD (view B) and the tone read of the lock: its state, the ship, the cone's half angle, the hold's progress (0 … 1). */
export function lockView(l: MissileLock, d: LockDials): { state: MissileLock['state']; ship: number | null; cone: number; progress: number } {
  return { state: l.state, ship: l.target, cone: d.cone, progress: l.state === 'lock' ? 1 : l.state === 'track' ? Math.min(1, l.held / d.hold) : 0 };
}
